import { useEffect, useState } from "react";

import { Box, Flex, ScrollArea, Table } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { EmptyMessage, Loader } from "components";
import { IRootState } from "config/redux";
import {
  REQUEST_CREDIT_PREVIOUS_ID,
  REQUEST_CREDIT_PREVIOUS_VIEW_ID,
  REQUEST_PENDING,
} from "constant/service";
import { CreditService } from "services";
import { CreditOptionsResponse, CreditPreviousRequestOverview, CreditResponse } from "types";
import { AppUtils } from "utils";

import { PreviousCreditRequestDetail } from "./previous-credit-request-detail";
import { PreviousCreditRequestRow } from "./previous-credit-request-row";

const TABLE_HEADERS = [
  "id",
  "status",
  "bank",
  "statusChangedAt",
  "grantedCreditAmount",
  "grantedInstallmentAmount",
  "securedLoanInstallment",
];

interface PreviousCreditRequestsProps {
  options: CreditOptionsResponse | undefined;
  selectedCreditId: number | null;
  setSelectedCreditId: (id: number | null) => void;
  navigateToCurrentRequest: () => void;
}

export const PreviousCreditRequests = ({
  options,
  selectedCreditId,
  setSelectedCreditId,
  navigateToCurrentRequest,
}: PreviousCreditRequestsProps) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [previousRequests, setPreviousRequests] = useState<CreditPreviousRequestOverview[]>([]);
  const [selectedCredit, setSelectedCredit] = useState<CreditResponse | null>(null);

  const previousRequestsServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_PREVIOUS_ID],
  );
  const creditViewServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_PREVIOUS_VIEW_ID],
  );

  const isLoadingList = previousRequestsServiceObject?.status === REQUEST_PENDING;
  const isLoadingCredit = creditViewServiceObject?.status === REQUEST_PENDING;

  useEffect(() => {
    CreditService.creditPreviousRequests(id!);
  }, [id]);

  useEffect(() => {
    if (selectedCreditId === null) {
      setSelectedCredit(null);
    }
  }, [selectedCreditId]);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      previousRequestsServiceObject,
      REQUEST_CREDIT_PREVIOUS_ID,
      (data) => setPreviousRequests(data.content),
    );
  }, [previousRequestsServiceObject]);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      creditViewServiceObject,
      REQUEST_CREDIT_PREVIOUS_VIEW_ID,
      (data) => setSelectedCredit(data),
    );
  }, [creditViewServiceObject]);

  const openPreviousRequest = (creditId: number): void => {
    setSelectedCreditId(creditId);
    CreditService.creditsPreviousOne(creditId);
  };

  // Show detail view when a credit is selected
  if (selectedCreditId !== null) {
    if (isLoadingCredit || !selectedCredit || !options) {
      return (
        <Flex align="center" justify="center" mih="200px">
          <Loader />
        </Flex>
      );
    }

    return (
      <PreviousCreditRequestDetail
        credit={selectedCredit}
        options={options}
        navigateToCurrentRequest={navigateToCurrentRequest}
      />
    );
  }

  // Show table view
  return (
    <Box>
      {isLoadingList ? (
        <Flex align="center" justify="center" mih="200px">
          <Loader />
        </Flex>
      ) : previousRequests?.length > 0 ? (
        <ScrollArea.Autosize mah={700} style={{ borderRadius: 16 }}>
          <Table
            verticalSpacing={"sm"}
            borderColor="gray.5"
            style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}
          >
            <Table.Thead
              c={"var(--mantine-color-secondary-6)"}
              fw={500}
              style={{ borderBottom: "1px solid var(--mantine-color-gray-5)" }}
            >
              <Table.Tr>
                {TABLE_HEADERS.map((header) => (
                  <Table.Th key={header}>{t(`credit.previousRequests.table.${header}`)}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody bg="gray.1">
              {previousRequests.map((request) => (
                <PreviousCreditRequestRow
                  key={request.id}
                  request={request}
                  onClick={() => openPreviousRequest(request.id)}
                />
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea.Autosize>
      ) : (
        <EmptyMessage message={t("credit.previousRequests.empty")} newVersion />
      )}
    </Box>
  );
};
